import { Link } from 'react-router-dom'
import { Button, InlineError, Media, Skeleton } from '../../components/ui'
import { SectionHeading, ServiceCollection } from '../../features/catalog'
import { useGetCategoriesQuery, useGetServicesQuery } from '../../services/catalogApi'
import { useTenant } from '../../tenant/useTenant'

const FEATURED_COUNT = 6

/** E2 — the category grid and the featured services teaser. */
export function HomeCatalog() {
  const { slug } = useTenant()
  const categories = useGetCategoriesQuery(slug)
  const services = useGetServicesQuery({ slug, perPage: FEATURED_COUNT })

  return (
    <>
      <section className="container-page py-section-gap-md md:py-section-gap-lg">
        <div className="mb-16 text-center">
          <span className="mb-4 block font-label-caps text-label-caps uppercase tracking-widest text-primary">
            Our expertise
          </span>
          <h2 className="font-display text-headline-md text-on-surface">Curated services</h2>
        </div>

        {categories.error ? (
          <InlineError error={categories.error} onRetry={categories.refetch} />
        ) : categories.isLoading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} shape="rect" className="aspect-[4/5] w-full" />
            ))}
          </div>
        ) : categories.data?.length ? (
          // Stitch's "Curated Services" treatment: a 4:5 image with the copy
          // sitting in a gradient that fades up out of the page background.
          <ul className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {categories.data.map((category) => (
              <li key={category.id}>
                <Link
                  to={`/${slug}/services?category=${category.id}`}
                  className="group relative block overflow-hidden rounded-lg border border-primary/5 bg-surface-container-low transition-all duration-300 hover:border-primary/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-surface-container-high">
                    <Media
                      src={category.image}
                      alt=""
                      kind="service"
                      className="transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-surface via-surface/90 to-surface/0 p-6">
                    <h3 className="mb-2 font-display text-headline-sm text-primary">{category.name}</h3>
                    <p className="mb-4 font-body-md text-body-md text-on-surface-variant">
                      {category.services_count === 1 ? '1 treatment' : `${category.services_count} treatments`}
                    </p>
                    <span className="inline-flex items-center font-label-caps text-label-caps uppercase text-primary underline-offset-4 decoration-primary/50 group-hover:underline">
                      Book now
                      <span className="material-symbols-outlined ml-2 text-[16px]">arrow_forward</span>
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="container-page pb-section-gap-md md:pb-section-gap-lg">
        <SectionHeading eyebrow="Featured" title="Guest favorites" align="center" />
        <ServiceCollection
          // Sliced as well as asked for: a backend that ignores per_page would
          // otherwise turn the teaser into the whole menu.
          services={(services.data?.items ?? []).slice(0, FEATURED_COUNT)}
          isLoading={services.isLoading}
          error={services.error}
          onRetry={services.refetch}
          emptyBody="The salon is updating its service menu."
        />
        {(services.data?.total ?? 0) > FEATURED_COUNT && (
          <div className="mt-16 flex justify-center">
            <Button to={`/${slug}/services`} variant="outlined" size="lg">
              View all services
            </Button>
          </div>
        )}
      </section>
    </>
  )
}
